import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import {
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  RadioGroup,
  Typography,
} from '@mui/material';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import axios from 'src/utils/axios';
import ModalDialogMUI6 from 'src/components/ModalDialogMUI6';
import TooltipMUI6 from 'src/components/TooltipMUI6';
import TextFieldFormik from 'src/components/TextFieldFormik/index';
import { addNotification } from 'src/actions/notificationActions';
import { NOTIFICATION_STATUS } from 'src/constants';

const NOTE_TYPES = [
  { code: 'incident', label: 'Incident (2 days)' },
  { code: 'known-issue', label: 'Known issue (2 weeks)' },
  { code: 'change', label: 'Change (30 days)' },
];

// She should never have to do date arithmetic to know what dies when, so this
// says it in words. A note in its last day reads "today", which is the cue to
// extend it or let it go.
function describeExpiry(expiresAt) {
  const msLeft = new Date(expiresAt).getTime() - Date.now();
  if (Number.isNaN(msLeft)) return { text: 'no expiry set', urgent: true };
  if (msLeft <= 0) return { text: 'expired', urgent: true };

  const hours = Math.floor(msLeft / (60 * 60 * 1000));
  if (hours < 1) return { text: 'expires in under an hour', urgent: true };
  if (hours < 24) return { text: `expires today, in ${hours}h`, urgent: true };

  const days = Math.round(hours / 24);
  return { text: `expires in ${days} day${days === 1 ? '' : 's'}`, urgent: days <= 1 };
}

// Operational notes that ride in every AI draft prompt: an outage happening
// now, a bug the team is working, a change that shipped last night. Nothing
// else in the system can know these - the Knowledge Base describes how CP
// works, and no database query reveals that texts were delayed this morning.
//
// Lives in the chat toolbar rather than behind a settings route on purpose: it
// gets used mid-incident, often from a phone, and navigating away from the
// conversation that prompted it is the thing most likely to stop it happening.
function AiOpsNotes() {
  const dispatch = useDispatch();
  const [enabled, setEnabled] = useState(false);
  const [notes, setNotes] = useState([]);
  const [maxActive, setMaxActive] = useState(10);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const loadNotes = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/ai-support-drafts/ops-notes');
      setEnabled(Boolean(data?.enabled));
      setNotes(data?.notes || []);
      if (data?.maxActive) setMaxActive(data.maxActive);
    } catch (error) {
      setEnabled(false);
    }
  }, []);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const formik = useFormik({
    initialValues: { noteType: 'incident', title: '', body: '' },
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        await axios.post('/api/ai-support-drafts/ops-notes', {
          noteType: values.noteType,
          title: values.title,
          body: values.body,
        });
        dispatch(addNotification('ops notes', 'Note added - it is in the next draft', NOTIFICATION_STATUS.SUCCESS));
        resetForm();
        setIsAdding(false);
        await loadNotes();
      } catch (error) {
        const message = error?.response?.data?.message || 'Note could not be saved';
        dispatch(addNotification('ops notes', message, NOTIFICATION_STATUS.ERROR));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    values, touched, errors, isSubmitting,
    handleChange, handleBlur, handleSubmit, resetForm,
  } = formik;

  const act = async (id, action) => {
    setIsBusy(true);
    try {
      await axios.patch(`/api/ai-support-drafts/ops-notes/${id}`, { action });
      await loadNotes();
    } catch (error) {
      const message = error?.response?.data?.message || 'Could not update that note';
      dispatch(addNotification('ops notes', message, NOTIFICATION_STATUS.ERROR));
    } finally {
      setIsBusy(false);
    }
  };

  if (!enabled) return null;

  const handleClose = () => {
    resetForm();
    setIsAdding(false);
    setIsModalVisible(false);
  };

  const expiringToday = notes.filter((note) => describeExpiry(note.expiresAt).urgent).length;
  const atCap = notes.length >= maxActive;
  const canSave = Boolean(values.title.trim() && values.body.trim());

  return (
    <>
      <TooltipMUI6
        title={notes.length
          ? `Ops notes in every draft: ${notes.length}${expiringToday ? ` (${expiringToday} expiring today)` : ''}`
          : 'Ops notes - none active'}
      >
        <IconButton
          aria-label={notes.length
            ? `Ops notes, ${notes.length} active`
            : 'Ops notes, none active'}
          size="small"
          color={expiringToday ? 'warning' : 'secondary'}
          onClick={() => setIsModalVisible(true)}
        >
          {/* The count was only ever in the tooltip, so she could not tell at a
              glance that notes are riding every draft - which is the one thing
              worth knowing without hovering. */}
          <Badge
            badgeContent={notes.length}
            color={expiringToday ? 'warning' : 'secondary'}
            overlap="circular"
            sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: '15px', minWidth: '15px' } }}
          >
            <CampaignOutlinedIcon fontSize="small" />
          </Badge>
        </IconButton>
      </TooltipMUI6>

      <ModalDialogMUI6
        isVisible={isModalVisible}
        title="Ops Notes"
        okText={isAdding ? 'Save note' : 'Add note'}
        closeText="Done"
        onOk={isAdding ? handleSubmit : () => setIsAdding(true)}
        onClose={handleClose}
        okDisabled={isAdding ? !canSave : atCap}
        loading={isSubmitting || isBusy}
        content={(
          <Box sx={{
            display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '320px', maxWidth: '520px',
          }}
          >
            <Typography variant="body2" color="textSecondary">
              These go into every AI draft, and outrank the Knowledge Base. Keep them
              short and factual.
            </Typography>

            {notes.length === 0 && !isAdding && (
              <Typography variant="body2">
                No active notes. Drafts are running on the Knowledge Base alone.
              </Typography>
            )}

            {notes.map((note) => {
              const expiry = describeExpiry(note.expiresAt);
              return (
                <Box key={note.id}>
                  <Divider sx={{ marginBottom: '8px' }} />
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap',
                  }}
                  >
                    <Chip
                      size="small"
                      variant="outlined"
                      label={note.status === 'resolved' ? 'Resolved' : note.noteType}
                      color={note.status === 'resolved' ? 'default' : 'secondary'}
                    />
                    <Typography variant="subtitle2" sx={{ flexShrink: 1 }}>
                      {note.title}
                    </Typography>
                    <Chip
                      size="small"
                      variant={expiry.urgent ? 'filled' : 'outlined'}
                      color={expiry.urgent ? 'warning' : 'default'}
                      label={expiry.text}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ margin: '4px 0 6px 0' }}>
                    {note.body}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: '8px' }}>
                    {note.status !== 'resolved' && (
                      <Button size="small" onClick={() => act(note.id, 'resolve')}>
                        Resolve
                      </Button>
                    )}
                    <Button size="small" onClick={() => act(note.id, 'extend')}>
                      Extend
                    </Button>
                    <Button size="small" color="error" onClick={() => act(note.id, 'remove')}>
                      Remove
                    </Button>
                  </Box>
                </Box>
              );
            })}

            {atCap && !isAdding && (
              <Typography variant="body2" color="error">
                {`That is ${maxActive} live notes. Resolve one that is finished before adding another.`}
              </Typography>
            )}

            {isAdding && (
              <>
                <Divider />
                <RadioGroup
                  name="noteType"
                  value={values.noteType}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  row
                >
                  {NOTE_TYPES.map((type) => (
                    <TextFieldFormik
                      key={type.code}
                      touchedValue={touched.noteType}
                      errorValue={errors.noteType}
                      name="noteType"
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      value={type.code}
                      formControlLabel={type.label}
                      sizeInput="small"
                      fieldType="radio"
                    />
                  ))}
                </RadioGroup>
                <TextFieldFormik
                  label="Title"
                  name="title"
                  value={values.title}
                  touchedValue={touched.title}
                  errorValue={errors.title}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  sizeInput="small"
                  placeholder="Inbound texts delayed"
                />
                <TextFieldFormik
                  label="What is happening"
                  name="body"
                  value={values.body}
                  touchedValue={touched.body}
                  errorValue={errors.body}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  sizeInput="small"
                  multiline
                  rows={3}
                  placeholder="Some inbound texts are delayed. Outbound is unaffected."
                />
                <Typography variant="caption" color="textSecondary">
                  Expiry is set for you from the type, and starts now. Extend it from
                  this list if it is still true when it runs out.
                </Typography>
              </>
            )}
          </Box>
        )}
      />
    </>
  );
}

export default AiOpsNotes;
